import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getFirestore, collection, addDoc, deleteDoc, doc, getDocs, updateDoc, query, orderBy } from 'firebase/firestore';
import { environment } from '../../../environments/environment';

export interface ContactLead {
  id?: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  serviceType?: string;
  createdAt?: string;
  updatedAt?: string;
  company?: string;
  isActive?: number;
  createdBy?: string;
  updatedBy?: string;
}

export interface GalleryItem {
  id?: string;
  title: string;
  url: string;
  images?: string[];
  category: string;
  caption?: string;
  createdAt?: string;
  updatedAt?: string;
  publicId?: string;
  publicIds?: string[];
  resourceType?: 'image' | 'video';
  resourceTypes?: Array<'image' | 'video'>;
  createdBy?: string;
  updatedBy?: string;
}

@Injectable({ providedIn: 'root' })
export class DatabaseService {
  private readonly localLeadsKey = 'aura_local_leads';
  private readonly leadSubject = new BehaviorSubject<ContactLead[]>([]);
  private app?: FirebaseApp;
  private db: any = null;

  constructor() {
    try {
      this.app = getApps().length ? getApps()[0] : initializeApp(environment.firebaseConfig);
      this.db = getFirestore(this.app);
    } catch (error) {
      console.warn('Firestore initialization skipped.', error);
      this.db = null;
    }
  }

  createLead(lead: ContactLead): Promise<void> {
    const payload = {
      ...lead,
      isActive: lead.isActive ?? 1,
      createdBy: lead.createdBy || 'user',
      updatedBy: lead.updatedBy || lead.createdBy || 'user',
      createdAt: new Date().toISOString()
    };
    if (!this.db) {
      this.storeLocalLead(payload);
      this.emitLeadUpdates();
      return Promise.resolve();
    }

    return addDoc(collection(this.db, 'contact_leads'), payload).then(() => undefined).catch((error) => {
      console.warn('Falling back to local lead storage.', error);
      this.storeLocalLead(payload);
    }).finally(() => {
      this.emitLeadUpdates();
    });
  }

  getLeads(): Observable<ContactLead[]> {
    this.emitLeadUpdates();
    return this.leadSubject.asObservable();
  }

  updateLead(id: string, lead: Partial<ContactLead>): Promise<void> {
    const payload = {
      ...lead,
      isActive: lead.isActive ?? 1,
      updatedBy: lead.updatedBy || 'staff',
      updatedAt: new Date().toISOString()
    };
    if (!this.db) {
      this.updateLocalLead(id, payload);
      this.emitLeadUpdates();
      return Promise.resolve();
    }

    return updateDoc(doc(this.db, 'contact_leads', id), payload).then(() => undefined).catch((error) => {
      console.warn('Unable to update remote lead; updating local copy.', error);
      this.updateLocalLead(id, payload);
    }).finally(() => {
      this.emitLeadUpdates();
    });
  }

  deleteLead(id: string): Promise<void> {
    if (!this.db) {
      this.softDeleteLocalLead(id);
      this.emitLeadUpdates();
      return Promise.resolve();
    }

    return updateDoc(doc(this.db, 'contact_leads', id), { isActive: 0, updatedAt: new Date().toISOString() }).then(() => undefined).catch((error) => {
      console.warn('Unable to update remote lead; soft deleting local copy.', error);
      this.softDeleteLocalLead(id);
    }).finally(() => {
      this.emitLeadUpdates();
    });
  }

  private emitLeadUpdates(): void {
    if (!this.db) {
      this.leadSubject.next(this.readLocalLeads());
      return;
    }

    const q = query(collection(this.db, 'contact_leads'), orderBy('createdAt', 'desc'));
    getDocs(q).then((snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as ContactLead) }));
      const localRows = this.readLocalLeads();
      const merged = [...localRows, ...rows.filter((row) => !localRows.some((existing) => existing.id === row.id))];
      this.leadSubject.next(merged);
    }).catch((error) => {
      console.warn('Firestore unavailable, using local lead list', error);
      this.leadSubject.next(this.readLocalLeads());
    });
  }

  private storeLocalLead(lead: ContactLead): void {
    if (typeof window === 'undefined') {
      return;
    }
    const existing = this.readLocalLeads();
    existing.unshift({ ...lead, id: `local-${Date.now()}` });
    localStorage.setItem(this.localLeadsKey, JSON.stringify(existing));
  }

  private readLocalLeads(): ContactLead[] {
    if (typeof window === 'undefined') {
      return [];
    }
    try {
      const raw = localStorage.getItem(this.localLeadsKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private updateLocalLead(id: string, updates: Partial<ContactLead>): void {
    if (typeof window === 'undefined') {
      return;
    }
    const existing = this.readLocalLeads();
    const updated = existing.map((lead) => (lead.id === id ? { ...lead, ...updates } : lead));
    localStorage.setItem(this.localLeadsKey, JSON.stringify(updated));
  }

  private softDeleteLocalLead(id: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    const existing = this.readLocalLeads();
    const updated = existing.map((lead) => (lead.id === id ? { ...lead, isActive: 0 } : lead));
    localStorage.setItem(this.localLeadsKey, JSON.stringify(updated));
  }

  private normalizeGalleryItem(item: GalleryItem): GalleryItem {
    const images = (item.images && item.images.length ? item.images : (item.url ? [item.url] : [])).filter(Boolean);
    const resourceTypes = (item.resourceTypes && item.resourceTypes.length ? item.resourceTypes : (item.resourceType ? [item.resourceType] : [])).filter(Boolean);
    const normalizedResourceType = resourceTypes[0] === 'video' ? 'video' : 'image';
    return {
      ...item,
      url: item.url || images[0] || '',
      images,
      publicIds: item.publicIds || (item.publicId ? [item.publicId] : []),
      resourceType: normalizedResourceType,
      resourceTypes: resourceTypes.length ? resourceTypes : [normalizedResourceType]
    };
  }

  createGalleryItem(item: GalleryItem): Promise<void> {
    const payload = {
      ...this.normalizeGalleryItem(item),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: item.createdBy || item.updatedBy || 'unknown',
      updatedBy: item.updatedBy || item.createdBy || 'unknown'
    };
    return addDoc(collection(this.db, 'gallery_items'), payload).then(() => undefined);
  }

  getGalleryItems(): Observable<GalleryItem[]> {
    return new Observable((observer) => {
      const q = query(collection(this.db, 'gallery_items'), orderBy('createdAt', 'desc'));
      getDocs(q).then((snapshot) => {
        const rows = snapshot.docs.map((docSnap) => this.normalizeGalleryItem({ id: docSnap.id, ...(docSnap.data() as GalleryItem) }));
        observer.next(rows);
        observer.complete();
      }).catch((error) => {
        console.warn('Gallery unavailable, using fallback.', error);
        observer.next([
          { title: 'AURA Residence', url: 'https://images.unsplash.com/photo-1512918728675-ed5a9ecdebfd?auto=format&fit=crop&w=1200&q=80', images: ['https://images.unsplash.com/photo-1512918728675-ed5a9ecdebfd?auto=format&fit=crop&w=1200&q=80'], category: 'Exterior' },
          { title: 'Signature Lounge', url: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=80', images: ['https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=80'], category: 'Interior' },
          { title: '3D Concept Deck', url: 'https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1200&q=80', images: ['https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1200&q=80'], category: '3D Design' }
        ]);
        observer.complete();
      });
    });
  }

  updateGalleryItem(id: string, category: string, userId?: string): Promise<void> {
    return updateDoc(doc(this.db, 'gallery_items', id), {
      category,
      updatedBy: userId || 'unknown',
      updatedAt: new Date().toISOString()
    }).then(() => undefined);
  }

  updateGalleryItemDetails(id: string, item: Partial<GalleryItem>, userId?: string): Promise<void> {
    return updateDoc(doc(this.db, 'gallery_items', id), {
      ...item,
      updatedBy: userId || item.updatedBy || 'unknown',
      updatedAt: new Date().toISOString()
    }).then(() => undefined);
  }

  deleteGalleryItem(id: string): Promise<void> {
    return deleteDoc(doc(this.db, 'gallery_items', id)).then(() => undefined);
  }
}
